<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import MasterLayout from "~/layouts/MasterLayout.vue";
import {useTwoFactorStore} from "~/plugins/store/two-factor";
import {
  createDefaultTwoFactorProvider,
  type GenerateQrCodeResponse,
  type TwoFactorProvider,
  type VerifyTwoFactorCodeResponse
} from "~/types/TwoFactor";
import {useAuthStore} from "~/plugins/store/auth";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useNuxtApp} from "#app";
import AuthTwoFactorCode from "~/components/auth/AuthTwoFactorCode.vue";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {useSystemStore} from "~/plugins/store/system";
import auth from "~/middleware/auth";
import AccountLayout from "~/layouts/AccountLayout.vue";

export default defineComponent({
  components: {AccountLayout, LoadingDialog, AuthTwoFactorCode, MasterLayout},
  setup() {
    definePageMeta({
      middleware: [
        'auth',
      ],
    });

    useHead({
      title: 'Xiga | 2FA',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');
    const isLoadingSkeleton = ref(true);
    const route = useRoute();
    const router = useRouter();
    const {$toast} = useNuxtApp();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const isTwoFactorCodeDisabled = ref<boolean>(false); // This is used to disable the two-factor code input field
    const isTwoFactorEnabled = ref<boolean>(false);
    const lastUrl = ref<string | null>('');
    const twoFactorStore = useTwoFactorStore();
    const generateQrCodeResponse = ref<GenerateQrCodeResponse | null>(null);
    const verifyTwoFactorCodeResponse = ref<VerifyTwoFactorCodeResponse | null>(null);
    const authStore = useAuthStore();
    const recaptchaInstance = useReCaptcha();
    const activeStep = ref(1);
    const twoFactorCode = ref<string | null>(null);
    const twoFactorProvider = ref<TwoFactorProvider>(createDefaultTwoFactorProvider());
    const recoveryKey = ref<string | null>(null);
    const copied = ref(false);
    const user = ref(authStore.getUser());


    // Create a computed property to access the isTwoFactorAuthRequired state
    const isTwoFactorAuthRequired = computed(() => twoFactorStore.isTwoFactorAuthRequired);

    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const stepperItems = ref(
        [
          {
            title: translation(translationList.value.scan, 'Selectarea și configurarea Providerului'),
            rules: [() => false],
          },
          {
            title: translation(translationList.value.validate_code, 'Introducerea codului și salvarea cheii de recuperare'),
          },
        ]);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se încarcă...');

      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1000);
      lastUrl.value = route.query.last_url as string;

      // Verifică dacă URL-ul curent conține parametrul și elimină-l dacă este cazul
      const currentPath = window.location.pathname;
      const basePath = '/account/2fa'; // Path-ul de bază pe care îl dorești fără parametri
      if (currentPath.startsWith(`${basePath}`)) {
        // Asigură-te că rămâne doar calea de bază
        const newUrl = `${window.location.origin}${basePath}`;

        // Păstrează starea existentă din history.state
        const currentState = history.state;
        history.replaceState(currentState, '', newUrl);
      }

      isLoadingSkeleton.value = false;
      console.log(user.value.is_two_factor_enabled)
      if (user.value) {
        if (user.value.is_two_factor_enabled) {
          isTwoFactorEnabled.value = user.value.is_two_factor_enabled;
          showLoadingDialog.value = false;
          return;
        }
      }

      const token = await recaptcha(recaptchaInstance);
      generateQrCodeResponse.value = await twoFactorStore.generateQrCode(<string>authStore.getAccessToken(), token);
      showLoadingDialog.value = false;
    });

    watch(() => route.fullPath, async (newValue) => {
      const currentPath = window.location.pathname;
      const basePath = '/account/2fa'; // Path-ul de bază pe care îl dorești fără parametri
      if (currentPath.startsWith(`${basePath}`)) {
        // Asigură-te că rămâne doar calea de bază
        const newUrl = `${window.location.origin}${basePath}`;

        // Păstrează starea existentă din history.state
        const currentState = history.state;
        history.replaceState(currentState, '', newUrl);
      }
    });

    watch(twoFactorProvider, async (newValue) => {
      if (newValue.code === 'authenticator') {
        const token = await recaptcha(recaptchaInstance);
        generateQrCodeResponse.value = await twoFactorStore.generateQrCode(<string>authStore.getAccessToken(), token);
      } else {
        generateQrCodeResponse.value = null;
      }
    });

    const scrollToTop = () => {
      const {$scrollToTop} = useNuxtApp();
      $scrollToTop();
    }

    const validateStep = async (step: number) => {
      let isFormValid = true;

      scrollToTop();

      switch (step) {
        case 1:
          if (twoFactorProvider.value.code === 'email' || twoFactorProvider.value.code === 'sms') {
            showLoadingDialog.value = true;
            messageLoadingDialog.value = translation(translationList.value.loading, 'Se încarcă...');
            const token = await recaptcha(recaptchaInstance);
            const {userAgent} = useDevice();
            const systemStore = useSystemStore();
            const response = await twoFactorStore.generateTwoFactorCode(
                <string>authStore.getAccessToken(),
                userAgent,
                <string>systemStore.clientIp,
                twoFactorProvider.value.code,
                token);
            if (response && response.is_sent) {
              recoveryKey.value = response.recovery_key ? response.recovery_key : null;
              $toast.success(translation(translationList.value.email_sent, 'Codul de verificare a fost trimis.'));
              activeStep.value = 2;
            } else {
              $toast.error(translation(translationList.value.email_not_sent, 'Codul de verificare nu a putut fi trimis.'));
              isFormValid = false;
            }
          } else {
            activeStep.value = 2;
          }
          showLoadingDialog.value = false;
          break;
        case 2:
          showLoadingDialog.value = true;
          messageLoadingDialog.value = translation(translationList.value.loading, 'Se încarcă...');
          if (twoFactorCode.value !== null) {
            const token = await recaptcha(recaptchaInstance);
            const {userAgent} = useDevice();
            const systemStore = useSystemStore();
            verifyTwoFactorCodeResponse.value = await twoFactorStore.verifyTwoFactorCode(
                <string>authStore.getAccessToken(),
                twoFactorCode.value,
                twoFactorProvider.value.code,
                userAgent,
                <string>systemStore.clientIp,
                token,
                generateQrCodeResponse.value?.secret);
            if (verifyTwoFactorCodeResponse.value?.is_valid) {
              if (verifyTwoFactorCodeResponse.value.recovery_key) {
                recoveryKey.value = verifyTwoFactorCodeResponse.value.recovery_key;
              }
              authStore.updateTwoFactorStatus(true);
              isTwoFactorCodeDisabled.value = true;
              $toast.success(translation(translationList.value.two_factor_code_valid, 'Autentificarea in doi pasi a fost activata cu succes.'));
            } else {
              $toast.error(translation(translationList.value.invalid_two_factor_code, 'Codul introdus este invalid.'));
            }
          } else {
            $toast.error(translation(translationList.value.invalid_two_factor_code, 'Codul introdus este invalid.'));
            isFormValid = false;
          }
          showLoadingDialog.value = false;
          break;
      }
    }

    const backToStep = (step: number) => {
      activeStep.value = step;
    };

    const copyToClipboard = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        $toast.success(translation(translationList.value.success_copy_to_clipboard, 'Codul a fost copiat în clipboard!'));
      } catch (error) {
        $toast.error(translation(translationList.value.error_copy_to_clipboard, 'Eroare la copierea codului.'));
      }
    };

    return {
      generateQrCodeResponse,
      translationList,
      translation,
      stepperItems,
      activeStep,
      validateStep,
      backToStep,
      twoFactorCode,
      messageLoadingDialog,
      showLoadingDialog,
      verifyTwoFactorCodeResponse,
      lastUrl,
      twoFactorProvider,
      recoveryKey,
      copied,
      copyToClipboard,
      isTwoFactorCodeDisabled,
      isTwoFactorEnabled,
      isLoadingSkeleton,
      isTwoFactorAuthRequired
    }
  }
});
</script>

<template>
  <account-layout class="profile">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
      <v-row>
        <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.my_profile, 'Autentificare în doi pași') }}
          </h3>
          <p class="font-weight-regular">
            <span
                v-html="translation(translationList.my_profile_helper, 'Aici puteți activa sau dezactiva autentificarea în doi pași pentru contul dumneavoastră.')"></span>
          </p>
        </v-col>
      </v-row>

      <v-skeleton-loader v-if="isLoadingSkeleton" class="skeleton-h-250" type="image"></v-skeleton-loader>

      <v-container v-else-if="!isTwoFactorEnabled" fluid class="centered-horizontally w-100">
        <v-stepper
            tile
            rounded="lg"
            min-height="500"
            elevation="0"
            :items="stepperItems"
            class="two-factor-stepper"
            selected-class="active"
            v-model="activeStep"
            :hide-actions="true"
        >
          <template v-slot:item.1>
            <div>
              <div v-if="isTwoFactorAuthRequired" class="mb-5">
                <v-alert type="warning" rounded="0" color="warning">
                  {{
                    translation(translationList.two_factor_required, 'Este obligatoriu să activezi autentificarea cu doi factori pentru a continua.')
                  }}
                </v-alert>
              </div>
              <v-row>
                <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                  <h3 class="font-weight-bold">
                    {{
                      translation(translationList.two_factor_enable, 'Activează Autentificarea în Doi Pași (2FA)')
                    }}</h3>
                  <p class="font-weight-regular">
                    {{
                      translation(translationList.two_factor_enable_helper, 'Alege cum dorești să faci autentificarea in doi pasi.')
                    }}
                  </p>
                </v-col>
              </v-row>
              <div v-if="twoFactorProvider.code === 'authenticator'">
                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <auth-two-factor-provider-select v-model="twoFactorProvider"/>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.scan_qrcode, 'Scanează codul QR') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.scan_qrcode_helper, 'Scanează codul QR afișat mai jos folosind aplicația ta de autentificare preferată (de exemplu, Google Authenticator).')
                      }}
                    </p>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <div v-if="generateQrCodeResponse">
                      <img :src="generateQrCodeResponse.qr_code" alt="QR Code">
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.add_code_manually, 'Introdu codul manual') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.add_code_manually_helper, 'Dacă nu poți scana codul QR, introdu manual acest cod în aplicația de autentificare.')
                      }}
                    </p>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <div v-if="generateQrCodeResponse">
                      <v-text-field
                          v-model="generateQrCodeResponse.secret"
                          dense
                          readonly
                          bg-color="surface"
                          variant="outlined"
                          append-inner-icon="mdi-content-copy"
                          @click:append-inner="copyToClipboard(generateQrCodeResponse.secret)"
                      ></v-text-field>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{
                        translation(translationList.download_2fa_app_title, 'Descarcă o aplicație de autentificare')
                      }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.download_2fa_app_helper, 'Dacă nu ai o aplicație de autentificare, poți descărca Google Authenticator gratuit de pe Google Play sau App Store.')
                      }}
                    </p>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="auto" md="6" class="d-flex flex-row justify-start centered-on-mobile">
                    <NuxtLink
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                        target="_blank" aria-label="Google Authenticator">
                      <NuxtImg
                          src="/images/googleplay.png"
                          alt="Google play"
                          height="50"
                          width="171"></NuxtImg>
                    </NuxtLink>
                    <NuxtLink href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank"
                              aria-label="Google Authenticator">
                      <NuxtImg
                          src="/images/appstore.png"
                          alt="App Store"
                          height="50"
                          width="171"></NuxtImg>
                    </NuxtLink>
                  </v-col>
                </v-row>
              </div>

              <v-row class="mt-10 mb-15">
                <v-col cols="12" class="d-flex align-center justify-center">
                  <v-btn
                      class="block-on-mobile"
                      color="primary"
                      size="x-large"
                      flat
                      rounded="lg"
                      @click="validateStep(1)"
                  >
                    {{ translation(translationList.step, 'Pasul') }}
                    {{ translation(translationList.next, 'Următor') }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </template>
          <template v-slot:item.2>
            <div class="d-flex flex-column justify-space-between" style="min-height: 500px;">
              <div>
                <div
                    v-if="verifyTwoFactorCodeResponse && verifyTwoFactorCodeResponse.is_valid && verifyTwoFactorCodeResponse.recovery_key"
                    class="mb-5">
                  <v-alert type="success" rounded="lg" color="success">
                    {{
                      translation(translationList.two_factor_activated, 'Ai activat cu succes autentificarea în doi pași.')
                    }}
                  </v-alert>
                </div>

                <v-row>
                  <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{
                        translation(translationList.verify_2fa_app_code, 'Validare cod din aplicație')
                      }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.verify_2fa_app_code_helper, 'Introdu codul generat de aplicația ta de autentificare pentru a valida configurarea.')
                      }}
                    </p>
                  </v-col>
                </v-row>
              </div>

              <div class="flex-grow-1 d-flex align-center">
                <v-row>
                  <v-col cols="12">
                    <auth-two-factor-code v-model="twoFactorCode"
                                          :is-disabled="isTwoFactorCodeDisabled"/>
                  </v-col>
                  <v-col cols="12" v-if="twoFactorProvider.code === 'email' || twoFactorProvider.code === 'sms'">
                    <div v-if="recoveryKey">
                      <p>{{ translation(translationList.two_factor_recovery_key, 'Cheia de recuperare') }}:
                        <br>
                        <strong>{{ recoveryKey }}</strong>
                        <br>
                        <strong>{{
                            translation(translationList.two_factor_recovery_key_copy, 'Copiază cheia de recuperare de mai sus pentru a o folosi în cazul în care nu poți accesa aplicația de autentificare. Atentie! Acest cod este unic și nu poate fi recuperat.')
                          }}</strong>
                      </p>
                    </div>
                  </v-col>
                  <v-row class="mx-1 d-flex justify-center" v-if="verifyTwoFactorCodeResponse && verifyTwoFactorCodeResponse.is_valid && verifyTwoFactorCodeResponse.recovery_key">
                    <v-col cols="12" sm="auto" md="12">
                      <div>
                        <v-alert type="info" rounded="lg" color="info">
                          {{
                            translation(translationList.two_factor_recovery_key, 'Salvează cheia de recuperare într-un loc sigur! Aceasta este singura modalitate de a dezactiva autentificarea în doi pași dacă nu mai poți accesa aplicația de autentificare.')
                          }}
                          <br>
                          {{
                            translation(translationList.two_factor_recovery_key_copy, 'Poți salva cheia într-un manager de parole sau pe un dispozitiv fizic (de exemplu, pe hârtie).')
                          }}
                        </v-alert>
                      </div>
                    </v-col>

                    <v-col cols="12" sm="5" md="5">
                      <v-text-field
                          v-model="recoveryKey"
                          dense
                          readonly
                          bg-color="surface"
                          variant="outlined"
                          append-inner-icon="mdi-content-copy"
                          label="Cheia de recuperare"
                          @click:append-inner="copyToClipboard(recoveryKey)"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-row>
              </div>

              <div>
                <v-row class="mb-15">
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-start centered-on-mobile"
                         v-if="!verifyTwoFactorCodeResponse || !verifyTwoFactorCodeResponse.is_valid"
                  >
                    <v-btn
                        class="block-on-mobile"
                        color="primary-light"
                        size="x-large"
                        variant="outlined"
                        rounded="lg"
                        @click="backToStep(1)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.previous, 'Anterior') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-end centered-on-mobile"
                         v-if="!verifyTwoFactorCodeResponse || !verifyTwoFactorCodeResponse.is_valid">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="lg"
                        @click="validateStep(2)"
                    >
                      {{ translation(translationList.validate, 'Validează') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-end centered-on-mobile"
                         v-else-if="lastUrl && lastUrl.length">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="lg"
                        @click="navigateTo(lastUrl)"
                    >
                      {{ translation(translationList.continue, 'Continua de unde ai ramas') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

            </div>
          </template>
        </v-stepper>
      </v-container>

      <div v-else>
        <v-row>
          <v-col cols="12">
            <v-alert type="success" rounded="lg" color="success">
              {{
                translation(translationList.two_factor_already_enabled, 'Autentificarea în doi pași este deja activată.')
              }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row v-if="!isTwoFactorAuthRequired" class="mt-10 mb-15">
          <v-col cols="12" class="d-flex flex-column align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="error"
                variant="outlined"
                size="x-large"
                rounded="lg"
                :disabled="isTwoFactorAuthRequired"
                @click="navigateTo('/account/2fa-check?disable')"
            >
              {{ translation(translationList.two_factor_remove, 'Dezactivează Two-Factor') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </account-layout>
</template>

<style scoped>

</style>
